import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listSuggestions,
  acceptSuggestion,
  dismissSuggestion,
  editAndAcceptSuggestion,
  getSuggestionReasoning,
} from '../services/aiSuggestionService';
import type { AiSuggestion, SuggestionReasoning } from '../types/aiSuggestion';

// Query key factory
const suggestionKeys = {
  all: ['ai-suggestions'] as const,
  byAquarium: (aquariumId: string) => ['ai-suggestions', 'aquarium', aquariumId] as const,
  reasoning: (suggestionId: string) => ['ai-suggestions', 'reasoning', suggestionId] as const,
};

// ─── List suggestions for an aquarium ────────────────────────────────────────

export interface UseAiSuggestionsOptions {
  aquariumId: string;
  enabled?: boolean;
}

export interface UseAiSuggestionsResult {
  suggestions: AiSuggestion[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useAiSuggestions(options: UseAiSuggestionsOptions): UseAiSuggestionsResult {
  const { aquariumId, enabled = true } = options;

  const query = useQuery({
    queryKey: suggestionKeys.byAquarium(aquariumId),
    queryFn: async (): Promise<AiSuggestion[]> => {
      const response = await listSuggestions(aquariumId);
      return response.data;
    },
    enabled: enabled && Boolean(aquariumId),
    staleTime: 60_000,
  });

  return {
    suggestions: query.data ?? [],
    isLoading: query.isPending && query.isFetching,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
  };
}

// ─── Suggestion reasoning query ───────────────────────────────────────────────

export interface UseSuggestionReasoningOptions {
  suggestionId: string;
  enabled?: boolean;
}

export interface UseSuggestionReasoningResult {
  reasoning: SuggestionReasoning | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export function useSuggestionReasoning(
  options: UseSuggestionReasoningOptions,
): UseSuggestionReasoningResult {
  const { suggestionId, enabled = true } = options;

  const query = useQuery({
    queryKey: suggestionKeys.reasoning(suggestionId),
    queryFn: async (): Promise<SuggestionReasoning> => {
      const response = await getSuggestionReasoning(suggestionId);
      return response.data;
    },
    enabled: enabled && Boolean(suggestionId),
    staleTime: 300_000,
  });

  return {
    reasoning: query.data ?? null,
    isLoading: query.isPending && query.isFetching,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
  };
}

// ─── Accept suggestion mutation ───────────────────────────────────────────────

export interface AcceptSuggestionVariables {
  suggestionId: string;
  aquariumId: string;
}

export interface UseAcceptSuggestionResult {
  acceptSuggestion: (vars: AcceptSuggestionVariables) => Promise<AiSuggestion>;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
}

export function useAcceptSuggestion(): UseAcceptSuggestionResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (vars: AcceptSuggestionVariables): Promise<AiSuggestion> => {
      const response = await acceptSuggestion(vars.suggestionId);
      return response.data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: suggestionKeys.byAquarium(vars.aquariumId),
      });
    },
  });

  return {
    acceptSuggestion: (vars: AcceptSuggestionVariables) => mutation.mutateAsync(vars),
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error instanceof Error ? mutation.error : null,
  };
}

// ─── Dismiss suggestion mutation ──────────────────────────────────────────────

export interface DismissSuggestionVariables {
  suggestionId: string;
  aquariumId: string;
}

export interface UseDismissSuggestionResult {
  dismissSuggestion: (vars: DismissSuggestionVariables) => Promise<AiSuggestion>;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
}

export function useDismissSuggestion(): UseDismissSuggestionResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (vars: DismissSuggestionVariables): Promise<AiSuggestion> => {
      const response = await dismissSuggestion(vars.suggestionId);
      return response.data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: suggestionKeys.byAquarium(vars.aquariumId),
      });
    },
  });

  return {
    dismissSuggestion: (vars: DismissSuggestionVariables) => mutation.mutateAsync(vars),
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error instanceof Error ? mutation.error : null,
  };
}

// ─── Edit-and-accept suggestion mutation ─────────────────────────────────────

export interface EditAndAcceptSuggestionVariables {
  suggestionId: string;
  aquariumId: string;
  /** Optional: the backend keeps the suggested title when omitted. */
  title?: string;
  description?: string;
  priority?: string;
  dueDate?: string;
  /** Edited dose amount (#1383) — positive, at most 10x the suggested amount. */
  amount?: number;
  /** Edited dose frequency (#1383). */
  frequency?: string;
}

export interface UseEditAndAcceptSuggestionResult {
  editAndAcceptSuggestion: (vars: EditAndAcceptSuggestionVariables) => Promise<AiSuggestion>;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
}

export function useEditAndAcceptSuggestion(): UseEditAndAcceptSuggestionResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (vars: EditAndAcceptSuggestionVariables): Promise<AiSuggestion> => {
      const { suggestionId, aquariumId: _aquariumId, ...editData } = vars;
      const response = await editAndAcceptSuggestion(suggestionId, editData);
      return response.data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: suggestionKeys.byAquarium(vars.aquariumId),
      });
    },
  });

  return {
    editAndAcceptSuggestion: (vars: EditAndAcceptSuggestionVariables) =>
      mutation.mutateAsync(vars),
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error instanceof Error ? mutation.error : null,
  };
}

// ─── Convenience hook combining all mutations ─────────────────────────────────

export interface UseSuggestionActionsResult {
  accept: (vars: AcceptSuggestionVariables) => Promise<AiSuggestion>;
  dismiss: (vars: DismissSuggestionVariables) => Promise<AiSuggestion>;
  editAndAccept: (vars: EditAndAcceptSuggestionVariables) => Promise<AiSuggestion>;
  isAccepting: boolean;
  isDismissing: boolean;
  isEditingAndAccepting: boolean;
  isPending: boolean;
}

export function useSuggestionActions(): UseSuggestionActionsResult {
  const acceptResult = useAcceptSuggestion();
  const dismissResult = useDismissSuggestion();
  const editAndAcceptResult = useEditAndAcceptSuggestion();

  return {
    accept: acceptResult.acceptSuggestion,
    dismiss: dismissResult.dismissSuggestion,
    editAndAccept: editAndAcceptResult.editAndAcceptSuggestion,
    isAccepting: acceptResult.isPending,
    isDismissing: dismissResult.isPending,
    isEditingAndAccepting: editAndAcceptResult.isPending,
    isPending:
      acceptResult.isPending || dismissResult.isPending || editAndAcceptResult.isPending,
  };
}
